import type { MenuKey } from "@safers/auth";

export type Area = "control" | "health" | "cctv";

/** 영역 순서 — 첫 화면은 이 순서로 권한 있는 첫 영역이다. */
export const AREAS: Area[] = ["control", "health", "cctv"];

export const AREA_MENU: Record<Area, MenuKey> = {
  control: "CONTROL",
  health: "HEALTH",
  cctv: "CCTV",
};

export const AREA_ROOT: Record<Area, string> = {
  control: "/control",
  health: "/health",
  cctv: "/cctv",
};

export const controlPath = (siteId?: number | string | null) =>
  siteId == null ? AREA_ROOT.control : `${AREA_ROOT.control}/site/${siteId}`;

export const healthPath = (siteId?: number | string | null) =>
  siteId == null ? AREA_ROOT.health : `${AREA_ROOT.health}/site/${siteId}`;

export const healthRecordsPath = (siteId: number | string) => `${healthPath(siteId)}/records`;

export const ME_PATH = "/me";
export const CCTV_DEBUG_PATH = "/cctv/debug";
export const LOGIN_PATH = "/login";
/** 홈 — 권한에 맞는 첫 화면으로 보낸다(`HomeRedirect`). */
export const HOME_PATH = "/";

export const SITE_PARAM = "siteId";

export const SITE_MATCH_PATTERNS = [
  `${AREA_ROOT.control}/site/:${SITE_PARAM}`,
  `${AREA_ROOT.health}/site/:${SITE_PARAM}`,
  `${AREA_ROOT.health}/site/:${SITE_PARAM}/records`,
] as const;

export function areaOf(pathname: string): Area | null {
  if (pathname === AREA_ROOT.control || pathname.startsWith(`${AREA_ROOT.control}/`)) {
    return "control";
  }
  if (pathname === AREA_ROOT.health || pathname.startsWith(`${AREA_ROOT.health}/`)) {
    return "health";
  }
  if (pathname === AREA_ROOT.cctv || pathname.startsWith(`${AREA_ROOT.cctv}/`)) return "cctv";
  return null;
}

export function isAreaRoot(pathname: string): boolean {
  if (areaOf(pathname) == null) return false;
  return pathname.split("/").filter(Boolean).length <= 3;
}

export function isPlayback(pathname: string): boolean {
  return pathname.endsWith("/playback");
}

export function areaPath(area: Area, siteId?: number | string | null): string {
  if (area === "control") return controlPath(siteId);
  if (area === "health") return healthPath(siteId);
  return cctvLivePath({ siteId });
}

export const LEGACY_REDIRECTS: { from: string; to: (siteId: string) => string }[] = [
  { from: "/site/:siteId", to: (id) => controlPath(id) },
  { from: "/safety-report/:siteId", to: (id) => healthRecordsPath(id) },
  { from: "/cctv-ai/:siteId", to: (id) => cctvLivePath({ siteId: id }) },
  { from: "/cctv-playback/:siteId", to: () => cctvPlaybackPath() },
  { from: `${AREA_ROOT.cctv}/site/:siteId`, to: (id) => cctvLivePath({ siteId: id }) },
  { from: `${AREA_ROOT.cctv}/site/:siteId/playback`, to: () => cctvPlaybackPath() },
];

export const CCTV_PLAYBACK_ROOT = `${AREA_ROOT.cctv}/playback`;

export interface CctvLiveParams {
  siteId?: number | string | null;
  cctvId?: number | string | null;
}

function withQuery(base: string, entries: [string, string | null | undefined][]): string {
  const params = new URLSearchParams();
  for (const [key, value] of entries) {
    if (value != null && value !== "") params.set(key, value);
  }
  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export function cctvLivePath({ siteId, cctvId }: CctvLiveParams = {}): string {
  return withQuery(AREA_ROOT.cctv, [
    ["site", siteId == null ? null : String(siteId)],
    ["cam", cctvId == null ? null : String(cctvId)],
  ]);
}

export function cctvPlaybackPath(cctvId?: number | string | null): string {
  return withQuery(CCTV_PLAYBACK_ROOT, [["cam", cctvId == null ? null : String(cctvId)]]);
}

export function mergeQuery(to: string, search: string, hash: string): string {
  if (!search) return `${to}${hash}`;
  const [base = to, own = ""] = to.split("?");
  const params = new URLSearchParams(own);
  for (const [key, value] of new URLSearchParams(search)) {
    if (!params.has(key)) params.set(key, value);
  }
  const query = params.toString();
  return `${base}${query ? `?${query}` : ""}${hash}`;
}
